import React from 'react';
import { arrayOf, bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { getSelectedLabelFromOptions, convertCategoriesToSelectTreeOptions } from '../../../util/search';

import css from './MainPanelHeader.module.css';

const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress,
    noResultsInfo,
    urlQueryParams,
    filterConfigs,
    listingCategories,
    onClearFilter,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Extract active filter pills
  const pills = [];
  if (urlQueryParams && filterConfigs) {
    const { listingFieldsConfig, defaultFiltersConfig } = filterConfigs;

    // Check default filters (category)
    defaultFiltersConfig.forEach(config => {
      if (config.schemaType === 'category' && config.nestedParams) {
        const hasValue = config.nestedParams.some(param => {
          const queryParam = `pub_${param}`;
          return urlQueryParams[queryParam];
        });

        if (hasValue && listingCategories) {
          const options = convertCategoriesToSelectTreeOptions(listingCategories);
          const pickedValues = {};
          config.nestedParams.forEach((param, index) => {
            const queryParam = `pub_${param}`;
            if (urlQueryParams[queryParam]) {
              pickedValues[`${config.key}${index + 1}`] = urlQueryParams[queryParam];
            }
          });

          const label = getSelectedLabelFromOptions(options, pickedValues, config.key);
          if (label) {
            pills.push({
              key: config.key,
              label,
              clearParams: config.nestedParams.map(p => `pub_${p}`),
            });
          }
        }
      }
    });

    // Check listing fields (enum types like tolLevel2, tolLevel5)
    listingFieldsConfig.forEach(config => {
      if (config.schemaType === 'enum' && config.filterConfig?.indexForSearch) {
        const queryParam = `pub_${config.key}`;
        const value = urlQueryParams[queryParam];

        if (value) {
          const option = config.enumOptions?.find(opt => opt.option === value);
          const label = option?.label || value;

          pills.push({
            key: config.key,
            label,
            clearParams: [queryParam],
          });
        }
      }
    });
  }

  const handleClear = clearParams => {
    if (onClearFilter) {
      const clearedValues = clearParams.reduce((acc, param) => {
        return { ...acc, [param]: null };
      }, {});
      onClearFilter(clearedValues);
    }
  };

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        <div className={css.searchResultSummary}>
          <span className={css.resultsFound}>
            {searchInProgress ? (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            ) : (
              <FormattedMessage
                id="MainPanelHeader.foundResults"
                values={{ count: resultsCount }}
              />
            )}
          </span>
          {pills.length > 0 && (
            <div className={css.pillContainer}>
              {pills.map(pill => (
                <span key={pill.key} className={css.pill}>
                  {pill.label}
                  <button
                    type="button"
                    className={css.pillCloseButton}
                    onClick={() => handleClear(pill.clearParams)}
                    aria-label="Clear filter"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

MainPanelHeader.defaultProps = {
  rootClassName: null,
  className: null,
  resultsCount: null,
  searchInProgress: false,
  sortByComponent: null,
  urlQueryParams: null,
  filterConfigs: null,
  listingCategories: null,
  onClearFilter: null,
};

MainPanelHeader.propTypes = {
  rootClassName: string,
  className: string,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  sortByComponent: node,
  urlQueryParams: object,
  filterConfigs: object,
  listingCategories: arrayOf(object),
  onClearFilter: func,
};

export default MainPanelHeader;
